import { Box, Button, Grid, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { textFieldStyles } from 'quoting/new-quote/helpers';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'routing';
import { SecondaryActionButton } from 'shared/components/secondary-button';
import { PERMISSIONS } from 'shared/constants';
import { ERPOptions, quoteStatusOptions } from 'shared/constants/quotes';
import { FormTextField, SelectField, FormDateField } from 'shared/controls';
import { useUserPermissions } from 'shared/hooks';
import { useExportQuoteToPdfMutation } from 'shared/mutations/export-quote-to-pdf-mutation';
import { useDeletePdfFile } from 'shared/mutations/pdf-delete-mutation';
import { useUploadQuoteFiles } from 'shared/mutations/pdf-upload-mutation';
import { useEditQuote, useGetQuote } from 'shared/mutations/quotes';
import { useAuthState } from 'shared/stores';
import { QuoteDoc, QuoteDocument, QuoteReviewFormState } from 'shared/types/quotes';
import { QuoteRouteParams } from 'shared/types/routing';
import { addPdfExtension, formatDateTime, formatDateTimeEST, hashFileName } from 'shared/utility';
import { QuotingNavigationHeader } from '../quoting-navigation-header';
import { QuoteReviewDocumentation } from './quote-review-documentation';
import { useQueryApprovers } from 'shared/queries/query-approvers';

const mainContentStyles: SxProps<Theme> = {
  padding: 2,
};

const containerStyles: SxProps<Theme> = {
  paddingTop: 2,
};

const infoWidgetValueDisplayStyles: SxProps<Theme> = {
  fontWeight: 'bold',
};

const widgetContainerStyes: SxProps<Theme> = {
  paddingTop: '1.3rem',
};

const quoteInfoSectionStyles: SxProps<Theme> = {
  padding: 2,
  backgroundColor: '#eeeeee',
};

const selectFieldStyles: SxProps = {
  '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' },
};
const formFieldStyles: SxProps = {
  '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
};

export function QuoteReview() {
  const history = useHistory();
  const { quoteId } = useParams<QuoteRouteParams>();
  const { data } = useGetQuote(quoteId);
  const editQuoteMutation = useEditQuote();
  const deletePdfMutation = useDeletePdfFile();
  const { enqueueSnackbar } = useSnackbar();
  const { approvers, isLoading: isLoadingApprovers, isFetching: isFetchingApprovers } = useQueryApprovers();
  const methods = useForm<QuoteReviewFormState>({
    defaultValues: data,
  });
  const pdfMutation = useExportQuoteToPdfMutation({ quoteId, dateSubmitted: data?.dateSubmitted || '' });
  const userPermissions = useUserPermissions();
  const hasFileAccessPermission: boolean = userPermissions.includes(
    PERMISSIONS['write'] || PERMISSIONS['global.write']
  );

  const {
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = methods;

  useEffect(() => {
    if (data) {
      reset({ ...data, quoteFilesToDelete: [] });
    }
  }, [reset, data]);

  const authState = useAuthState();
  const uploadPdfMutation = useUploadQuoteFiles();

  const handleFiles = async (files: FileList | undefined, index: number, user: string, isoDate: string) => {
    const originalDocumentName = files?.[index].name || '';
    const fileId = hashFileName(`${quoteId}${originalDocumentName}${isoDate}`);
    const formData = new FormData();
    const fileToUpload = files ? [files[index]] : [];
    formData.append('file', new File(fileToUpload, addPdfExtension(fileId)));

    await uploadPdfMutation.mutateAsync(formData);
  };

  const originalStatus = data?.status;
  const currentStatus = watch('status');
  const isApprovedOrReviewedStatus = currentStatus === 'APPROVED' || currentStatus === 'REVIEWED';
  const isApprovedStatus = currentStatus === 'APPROVED';
  const isRevisionApprovedByRequired =
    !!originalStatus &&
    originalStatus === 'REVISION REQUESTED' &&
    (currentStatus === 'APPROVED' || currentStatus === 'IN PROCESS' || currentStatus === 'REVIEWED');

  const onSave = async (formData: QuoteReviewFormState) => {
    try {
      const user = authState.user.value?.displayName.toUpperCase() || '';
      const currentDate = new Date().toISOString();
      if (isApprovedOrReviewedStatus) {
        data?.products.forEach(p => p.createdDate = undefined)
      }
      let quote: QuoteDocument = {
        ...data!,
        products: data?.products || [],
        statusAndApprovalNotes: formData.statusAndApprovalNotes?.toUpperCase(),
        approvedBy: formData.approvedBy?.toUpperCase(),
        revisionApprovedBy: formData.revisionApprovedBy?.toUpperCase(),
        erp: formData.erp?.toUpperCase(),
        quoteNumber: formData.quoteNumber?.toUpperCase(),
        dateApproved: isApprovedOrReviewedStatus ? formData.dateApproved : '',
        status: formData.status?.toUpperCase(),
        quoteFilesToDelete: [],
        lastModifiedBy: user,
        lastModifiedOn: currentDate,
      };

      if (
        !hasFileAccessPermission &&
        (!!formData.quoteFilesToDelete || formData?.additionalDocuments || formData?.quoteFiles)
      ) {
        throw new Error('User does not have data load permission.');
      }

      if (!!formData.quoteFilesToDelete) {
        formData.quoteFilesToDelete.forEach(async file => {
          await deletePdfMutation.mutateAsync(addPdfExtension(file.id));
        });
      }

      const quoteIdsMarkedForDeletion = !!formData.quoteFilesToDelete ? formData.quoteFilesToDelete.map(q => q.id) : [];
      let updatedQuoteDocumentation: QuoteDoc[] = quote.quoteDocumentation
        ? [...quote.quoteDocumentation.filter(q => !quoteIdsMarkedForDeletion.includes(q.id))]
        : [];

      if (formData?.additionalDocuments) {
        for (let i = 0; i < formData?.additionalDocuments?.length; i++) {
          const fileDate = formatDateTime(new Date().getTime());
          const isoDate = new Date().toISOString();
          handleFiles(formData.additionalDocuments, i, user, isoDate);
          updatedQuoteDocumentation.push({
            id: hashFileName(`${quote.id}${formData.additionalDocuments?.[i].name || ''}${isoDate}`),
            documentType: 'ADDITIONAL DOCUMENTATION',
            documentName: formData.additionalDocuments?.[i].name || '',
            user: user,
            uploadDate: fileDate,
          });
        }
      }

      if (formData?.quoteFiles) {
        for (let i = 0; i < formData?.quoteFiles?.length; i++) {
          const fileDate = formatDateTime(new Date().getTime());
          const isoDate = new Date().toISOString();
          handleFiles(formData.quoteFiles, i, user, isoDate);
          updatedQuoteDocumentation.push({
            id: hashFileName(`${quote.id}${formData.quoteFiles?.[i].name || ''}${isoDate}`),
            documentType: 'QUOTE',
            documentName: formData.quoteFiles?.[i].name || '',
            user: user,
            uploadDate: fileDate,
          });
        }
      }

      quote.quoteDocumentation = [...updatedQuoteDocumentation];

      await editQuoteMutation.mutateAsync(quote);
      history.push(ROUTES.quote);
      enqueueSnackbar('Quote was saved.', { variant: 'success' });
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Quote was not saved.', { variant: 'error' });
    }
  };

  useEffect(() => {
    const currentApprovedDate = getValues('dateApproved');
    if (isApprovedOrReviewedStatus && !currentApprovedDate) {
      setValue('dateApproved', new Date().toISOString());
    }
  }, [getValues, setValue, isApprovedOrReviewedStatus]);

  const onExportPDF = async () => {
    await pdfMutation.mutate();
  };

  const formatSubmittedDate = (value: string | number | undefined) => {
    // for already existing dates as string in format of 05/11/2022
    // after deleting legacy date from all envs leave only formatDateTimeEST(new Date(value))
    if (typeof value === 'number' || value?.includes('Z')) {
      return formatDateTimeEST(new Date(value));
    } else {
      return value;
    }
  };

  const isFormLoading = editQuoteMutation.isLoading || uploadPdfMutation.isLoading;
  return (
    <FormProvider {...methods}>
      <form aria-label='form' id='quote-detail-form' onSubmit={handleSubmit(onSave)}>
        <Box sx={mainContentStyles}>
          <QuotingNavigationHeader selectedTab='quoteReview' />
          <Box sx={containerStyles}>
            <Typography align='center' variant='h4'>
              Quote Review
            </Typography>
            <Box sx={widgetContainerStyes}>
              <Grid container sx={quoteInfoSectionStyles}>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Customer</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.wholesalerCustomerNumber ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Project Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.jobInfoProjectName ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Wholesaler</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.wholesaler ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Plumber</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.plumberContractor ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Brand</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.brand ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Rep.</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {data?.requestedBy ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={1.7}>
                  <Grid item xs={12}>
                    <Typography variant='body2'>Date Submitted</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={infoWidgetValueDisplayStyles} variant='body2'>
                      {formatSubmittedDate(data?.dateSubmitted) ?? ''}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ paddingTop: 2 }}>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={2.4}>
                    <SelectField
                      name='status'
                      label='Quote Status'
                      defaultValue={data?.status}
                      options={[...quoteStatusOptions]}
                      getOptionLabel={opt => opt}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return true;
                        return option === value;
                      }}
                      sx={selectFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={2.4}>
                    <SelectField
                      name='approvedBy'
                      label='Approved/Reviewed By'
                      options={approvers || []}
                      getOptionLabel={opt => opt}
                      loading={isLoadingApprovers || isFetchingApprovers}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return true;
                        return option === value;
                      }}
                      sx={selectFieldStyles}
                      rules={{
                        validate: (value: string) =>
                          getValues('status') === 'DRAFT' || !!value || 'Director is required',
                      }}
                      error={Boolean(errors?.approvedBy)}
                      helperText={errors?.approvedBy?.message}
                    />
                  </Grid>
                  <Grid item xs={2.4}>
                    <SelectField
                      name='revisionApprovedBy'
                      label='Revision Approved By'
                      options={approvers || []}
                      getOptionLabel={opt => opt}
                      loading={isLoadingApprovers || isFetchingApprovers}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return true;
                        return option === value;
                      }}
                      sx={selectFieldStyles}
                      rules={{
                        required: {
                          message: 'Revision Approved By field is required.',
                          value: isRevisionApprovedByRequired,
                        },
                      }}
                      error={Boolean(errors?.revisionApprovedBy)}
                      helperText={errors?.revisionApprovedBy?.message}
                    />
                  </Grid>
                  <Grid item xs={2.4}>
                    <FormTextField
                      sx={formFieldStyles}
                      name='quoteNumber'
                      label='Quote Number'
                      rules={{
                        required: {
                          message: 'Quote Number field is required.',
                          value: isApprovedOrReviewedStatus,
                        },
                      }}
                      error={Boolean(errors?.quoteNumber)}
                      helperText={errors?.quoteNumber?.message}
                    />
                  </Grid>
                  <Grid item xs={2.4}>
                    <SelectField
                      name='erp'
                      label='ERP'
                      options={[...ERPOptions]}
                      getOptionLabel={opt => opt}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return true;
                        return option === value;
                      }}
                      sx={selectFieldStyles}
                      rules={{
                        required: {
                          message: 'ERP field is required.',
                          value: isApprovedOrReviewedStatus,
                        },
                      }}
                      error={Boolean(errors?.erp)}
                      helperText={errors?.erp?.message}
                    />
                  </Grid>
                  <Grid item xs={2.4}>
                    {isApprovedOrReviewedStatus && (
                      <FormDateField sx={textFieldStyles} name='dateApproved' label='Approved' disabled />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box>
              <Grid container item xs={12}>
                <Box width={'100%'}>
                  <QuoteReviewDocumentation quote={data} />
                </Box>
              </Grid>
              <Stack direction={'row'} spacing={3} height={'250px'} paddingTop={3}>
                <Grid item container xs={6} sx={{ height: '100%' }}>
                  <Grid item sx={{ height: '100%', width: '100%' }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 'bold', height: '40px', alignItems: 'center' }}>
                      Status and Approval Notes
                    </Typography>
                    <FormTextField
                      sx={formFieldStyles}
                      label='Status and Approval Notes'
                      name='statusAndApprovalNotes'
                      fullWidth
                      multiline
                      rows={5}
                      rules={{
                        required: {
                          message: 'Notes field is required.',
                          value: isApprovedStatus,
                        },
                      }}
                      error={Boolean(errors?.statusAndApprovalNotes)}
                      helperText={errors?.statusAndApprovalNotes?.message}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={6}>
                  <Box width={'100%'}></Box>
                </Grid>
              </Stack>
            </Box>
            <Grid item container xs={12} justifyContent='space-between'>
              <Box paddingTop={3}>
                <SecondaryActionButton actionText='Export Quote Request Form' type='button' onClick={onExportPDF} />
              </Box>
              <Box paddingTop={3}>
                <Button variant='contained' color='secondary' type='submit' disabled={isFormLoading || !isDirty}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
}
